import React, { useCallback, useEffect, useState } from "react";
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    Space,
    message,
    Popconfirm,
    Avatar,
    Typography,
    Tabs,
} from "antd";
import {
    PlusOutlined,
    FileImageOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import { api } from "../api";
import { fmtDate } from "../utils";
import ImageUpload from "../components/ImageUpload";
import GalleryUpload from "../components/GalleryUpload";

const { Text } = Typography;

// Languages the site supports; posts carry title/description for each.
const LANGS = [
    { code: "hy", label: "Հայերեն" },
    { code: "en", label: "English" },
    { code: "ru", label: "Русский" },
];

// A localized field is an object { hy, en, ru }; legacy items may be a plain
// string. Resolve one for table display, or expand into a full object for the form.
const displayLocalized = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    return field.hy || field.en || field.ru || "";
};
const toLocalized = (field) => {
    if (!field) return { hy: "", en: "", ru: "" };
    if (typeof field === "string") return { hy: field, en: "", ru: "" };
    return { hy: field.hy || "", en: field.en || "", ru: field.ru || "" };
};

// Manages the image gallery shown on the public website's Events page.
const PortfolioPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.listPortfolio();
            setItems(res.items);
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const openEdit = (r) => {
        setEditing(r);
        form.setFieldsValue({
            ...r,
            title: toLocalized(r.title),
            description: toLocalized(r.description),
        });
    };
    const openCreate = () => {
        setEditing({});
        form.resetFields();
        // New items go to the end by default.
        form.setFieldsValue({
            order: items.length,
            title: { hy: "", en: "", ru: "" },
            description: { hy: "", en: "", ru: "" },
        });
    };

    const save = async () => {
        const v = await form.validateFields();
        if (!v.image) {
            message.error("Please upload or paste an image");
            return;
        }
        setSaving(true);
        try {
            if (editing._id) {
                await api.updatePortfolio(editing._id, v);
                message.success("Updated");
            } else {
                await api.createPortfolio(v);
                message.success("Added");
            }
            setEditing(null);
            load();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.deletePortfolio(id);
            message.success("Deleted");
            load();
        } catch (e) {
            message.error(e.message);
        }
    };

    const columns = [
        {
            title: "Image",
            key: "image",
            width: 90,
            render: (_, r) => (
                <Avatar
                    shape="square"
                    size={56}
                    src={r.image}
                    icon={<FileImageOutlined />}
                />
            ),
        },
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            render: (v) => displayLocalized(v) || <Text type="secondary">—</Text>,
        },
        {
            title: "Photos",
            key: "photos",
            width: 90,
            render: (_, r) => 1 + (Array.isArray(r.images) ? r.images.length : 0),
        },
        {
            title: "Order",
            dataIndex: "order",
            key: "order",
            width: 90,
            defaultSortOrder: "ascend",
            sorter: (a, b) => (a.order || 0) - (b.order || 0),
        },
        {
            title: "Added",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 160,
            render: (d) => fmtDate(d, true),
        },
        {
            title: "Actions",
            key: "actions",
            width: 160,
            render: (_, r) => (
                <Space>
                    <Button size="small" onClick={() => openEdit(r)}>
                        Edit
                    </Button>
                    <Popconfirm title="Delete this image?" onConfirm={() => remove(r._id)}>
                        <Button size="small" danger>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // Per-language title + description tabs. Fields use nested names so the form
    // value is { title: { hy, en, ru }, description: { hy, en, ru } }.
    const langTabs = LANGS.map((l) => ({
        key: l.code,
        label: l.label,
        forceRender: true,
        children: (
            <>
                <Form.Item name={["title", l.code]} label="Title">
                    <Input placeholder="e.g. Beach cleanup, Yerevan" />
                </Form.Item>
                <Form.Item
                    name={["description", l.code]}
                    label="Description"
                    tooltip="Shown on the event's detail page"
                >
                    <Input.TextArea rows={5} placeholder="Tell the story of this event…" />
                </Form.Item>
            </>
        ),
    }));

    return (
        <div>
            <div className="admin-page-head">
                <h1 className="admin-page-title">Portfolio (Events gallery)</h1>
                <Space>
                    <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
                        Refresh
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        Add image
                    </Button>
                </Space>
            </div>

            <Text type="secondary">
                These images appear on the website's public Events page. Title and
                description can be written per language; lower “order” shows first.
            </Text>

            <Table
                rowKey="_id"
                loading={loading}
                columns={columns}
                dataSource={items}
                scroll={{ x: "max-content" }}
                style={{ marginTop: 16 }}
            />

            <Modal
                open={!!editing}
                title={editing && editing._id ? "Edit image" : "Add image"}
                onCancel={() => setEditing(null)}
                onOk={save}
                confirmLoading={saving}
                width={560}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Tabs items={langTabs} />
                    <Form.Item
                        name="image"
                        label="Cover image"
                        rules={[{ required: true, message: "A cover image is required" }]}
                    >
                        <ImageUpload />
                    </Form.Item>
                    <Form.Item
                        name="images"
                        label="More photos"
                        tooltip="Extra photos shown on the detail page"
                    >
                        <GalleryUpload />
                    </Form.Item>
                    <Form.Item name="order" label="Order" tooltip="Lower shows first">
                        <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PortfolioPage;
